import {last} from 'lodash';
import React, {type RefObject} from 'react';
import {Virtuoso, type VirtuosoHandle} from 'react-virtuoso';

import {useAutoScroll} from './hooks/useAutoScroll';
import {useFirstItemIndex} from './hooks/useFirstItemIndex';
import {useLogListScroller} from './hooks/useLogListScroller';
import {type LogSearchMatches} from './hooks/useLogSearch';
import {useVirtuosoComponents} from './hooks/useVirtuosoComponents';
import {type LogViewerState, useVirtuosoState} from './hooks/useVirtuosoState';
import {LogJumpButton} from './LogJumpButton';
import {type LogLabelFilterOption} from './LogLabelFilter';
import {type LogLineData, LogLineRenderer} from './LogLineRenderer';
import {type LogTimestampFormatters} from './logTimestamp';
import {type OnRetryFetchMore} from './LogViewerFetchMoreError';
import {type RenderFetchMoreError} from './LogViewerPageLoader';

const DENSE_LOG_EXTRA_RENDERED_ROW_COUNT = 150;

export interface DenseLogListProps {
  filteredLabels: readonly LogLabelFilterOption[];
  filteredLines: readonly LogLineData[];
  formatters: LogTimestampFormatters;
  isPolling?: boolean;
  isTimestampVisible: boolean;
  lineHeight: number;
  maxLabelLength: number;
  onJumpToBottom?: () => void;
  scrollTo: (row: number, align?: 'start' | 'center' | 'end') => void;
  virtuosoRef: React.RefObject<VirtuosoHandle | null>;
  // Search highlighting
  searchActive: number;
  searchMatches: LogSearchMatches;
  searchText: string;
  // Jump-to-bottom is only surfaced under improved pagination.
  isImprovedPaginationEnabled: boolean;
  // State persistence
  restoreState?: LogViewerState;
  onStateSaved?: (state: LogViewerState) => void;
  // Backward pagination (older logs)
  hasPrevious?: boolean;
  isLoadingPrevious?: boolean;
  isRetryingPrevious?: boolean;
  loadPreviousError?: Error | string;
  onLoadPrevious?: () => void;
  onRetryPrevious?: OnRetryFetchMore;
  renderFetchPreviousError?: RenderFetchMoreError;
  // Forward pagination (newer logs)
  isLoadingNext?: boolean;
  isRetryingNext?: boolean;
  loadNextError?: Error | string;
  onRetryNext?: OnRetryFetchMore;
  renderFetchNextError?: RenderFetchMoreError;
}

/**
 * Contiguous, array-backed log list: a sliding window over the loaded lines
 * with cursor pagination off the top/bottom. Used for the legacy log path and
 * as the search/filter fallback under global pagination. Owns first-item-index
 * bookkeeping, scroll-state persistence, and search highlighting.
 */
export const DenseLogList: React.FC<DenseLogListProps> = ({
  filteredLabels,
  filteredLines,
  formatters,
  isPolling,
  isTimestampVisible,
  lineHeight,
  maxLabelLength,
  onJumpToBottom,
  scrollTo,
  virtuosoRef,
  searchActive,
  searchMatches,
  searchText,
  isImprovedPaginationEnabled,
  restoreState,
  onStateSaved,
  hasPrevious,
  isLoadingPrevious,
  isRetryingPrevious,
  loadPreviousError,
  onLoadPrevious,
  onRetryPrevious,
  renderFetchPreviousError,
  isLoadingNext,
  isRetryingNext,
  loadNextError,
  onRetryNext,
  renderFetchNextError,
}) => {
  const firstItemIndex = useFirstItemIndex(
    filteredLines,
    (isLoadingPrevious || isRetryingPrevious) ?? false,
    restoreState?.firstItemIndex
  );

  const computeItemKey = (adjustedIndex: number) => {
    const index = adjustedIndex - firstItemIndex;
    return filteredLines[index]?.id ?? adjustedIndex;
  };

  const startReached = () => {
    if (
      !onLoadPrevious ||
      !hasPrevious ||
      isLoadingPrevious ||
      isRetryingPrevious
    ) {
      return;
    }

    onLoadPrevious();
  };

  const scrollToBottom = () => {
    const bottomIndex = filteredLines.length - 1;
    if (bottomIndex >= 0) {
      scrollTo(bottomIndex, 'end');
    }
  };

  // set when the list just mounted from the global list on a fresh search: the
  // sync scrollTo in useLogSearch fired against the old (global) ref, so seed
  // the mount position (and start unpinned) from the match here instead.
  const activeMatchRow = searchMatches.ordered[searchActive]?.row;

  const {captureState} = useVirtuosoState(virtuosoRef, onStateSaved);

  const {
    followOutput,
    handleAtBottomStateChange,
    handleIsScrolling: handleIsScrollingForAutoScroll,
    handleJumpToBottom,
    handleRangeChanged,
    handleScrollerRef,
    showJumpToBottom,
  } = useAutoScroll({
    firstItemIndex,
    startsUnpinned: !!restoreState || activeMatchRow != null,
    lineCount: filteredLines.length,
    onJumpToBottom,
    scrollToBottom,
  });

  const handleIsScrolling = (scrolling: boolean) => {
    handleIsScrollingForAutoScroll(scrolling);

    // Prefer updating state when scrolling stops as opposed to any range
    // change as the additional granularity isn't worth the excessive state
    // capturing.
    if (!scrolling) {
      captureState(firstItemIndex);
    }
  };

  // Find longest visible lineNum to help us align log contents.
  const maxLineNumLength = last(filteredLines)?.lineNum?.toString().length ?? 0;
  const {measurer, heightEstimates, setScrollerRef} = useLogListScroller({
    handleScrollerRef,
    lineHeight,
    lines: filteredLines,
    maxLabelLength,
    maxLineNumLength,
    showLabel: filteredLabels.length > 0,
    showLineNumber: maxLineNumLength > 0,
    showTimestamp: isTimestampVisible,
  });

  const itemContent = (index: number) => {
    const adjustedIndex = index - firstItemIndex;
    const line = filteredLines[adjustedIndex];

    return (
      <LogLineRenderer
        line={line}
        index={adjustedIndex}
        showTimestamp={isTimestampVisible}
        formatters={formatters}
        showLabel={filteredLabels.length > 0}
        showLineNumber={line.lineNum != null}
        maxLineNumLength={maxLineNumLength}
        maxLabelLength={filteredLabels.length > 0 ? maxLabelLength : 0}
        searchText={searchText}
        searchMatches={searchMatches}
        searchActive={searchActive}
        className="px-16 py-2"
      />
    );
  };

  const {components, context} = useVirtuosoComponents({
    isPolling: isPolling ?? false,
    isLoadingPrevious: isLoadingPrevious ?? false,
    isRetryingPrevious: isRetryingPrevious ?? false,
    loadPreviousError,
    onRetryPrevious,
    renderFetchPreviousError,
    isLoadingNext: isLoadingNext ?? false,
    isRetryingNext: isRetryingNext ?? false,
    loadNextError,
    onRetryNext,
    renderFetchNextError,
  });

  // an active search match wins the initial position (see activeMatchRow above);
  // it and restore are mutually exclusive so they can't fight over Virtuoso's
  // initial-position signal. otherwise, when restoring from state,
  // `initialTopMostItemIndex` must *not* be passed in; setting it (even to
  // `undefined`) overrides the loaded scroll position.
  const initialTopMostItemIndex =
    activeMatchRow != null
      ? {
          initialTopMostItemIndex: {
            index: activeMatchRow,
            align: 'center' as const,
          },
        }
      : restoreState
        ? {}
        : {initialTopMostItemIndex: Math.max(filteredLines.length - 1, 0)};

  return (
    <>
      {
        // Render a hidden element used to determine the count of characters
        // that fit on a single line and the width of the scrollbar.
        measurer
      }
      <Virtuoso
        // Use 'atBottomStateChange' instead of 'endReached' since 'endReached'
        // is only triggered once (until log data changes); this ensures users
        // can try to fetch new logs repeatedly.
        atBottomStateChange={handleAtBottomStateChange}
        components={components}
        computeItemKey={computeItemKey}
        context={context}
        data={filteredLines}
        firstItemIndex={firstItemIndex}
        defaultItemHeight={lineHeight + 4}
        followOutput={followOutput}
        heightEstimates={heightEstimates ?? undefined}
        {...initialTopMostItemIndex}
        isScrolling={handleIsScrolling}
        itemContent={itemContent}
        // Render additional logs to improve scroll performance for large lists.
        // Note that this value is arbitrary, but mostly eliminates flickering
        // when scrolling rapidly.
        increaseViewportBy={
          DENSE_LOG_EXTRA_RENDERED_ROW_COUNT * (lineHeight + 4)
        }
        rangeChanged={handleRangeChanged}
        ref={virtuosoRef as RefObject<VirtuosoHandle>}
        restoreStateFrom={
          activeMatchRow != null ? undefined : restoreState?.snapshot
        }
        scrollerRef={setScrollerRef}
        // Prevent flickering when scrolling backwards; for context, see
        // https://github.com/petyosi/react-virtuoso/issues/1096#issuecomment-2262255320.
        skipAnimationFrameInResizeObserver={true}
        startReached={startReached}
        style={{height: '100%'}}
      />
      {isImprovedPaginationEnabled && showJumpToBottom && (
        <LogJumpButton
          icon="chevron-down"
          label="Jump to bottom"
          position="bottom"
          onPress={handleJumpToBottom}
        />
      )}
    </>
  );
};
